import { Alert } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogButtons } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { FeatureFlagsType, getFeatureFlag } from '../../config/launch-darkly/FeatureFlagsController';
import { UserLevel } from '../../organization/users/enums/UserLevel';
import { UserRole } from '../../organization/users/enums/UserRole';
import { User } from '../../organization/users/models/User';
import { OrganizationUsersRepository } from '../../organization/users/services/OrganizationUsersRepository';
import { PrimaryButton, SecondaryButton } from '../components/Buttons';
import { CreateUserForm } from '../components/CreateUserForm';
import { FormSummary } from '../components/FormSummary';

type Props = {
  onClosed: (userId: number | undefined, assignTestersClicked: boolean) => void;
};

export const CreateUserFlow = ({ onClosed }: Props) => {
  const [userService] = useState(new OrganizationUsersRepository());
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<{ user: User; password: string } | null>(null);

  const useRoles = Boolean(getFeatureFlag(FeatureFlagsType.ENABLE_ORGANIZATION_USER_ROLES));

  // Regardless of the authorization model used, both level and roles are queried for the new user.
  // The backend maps roles onto the level that best matches them: if we create a user with certain
  // roles and the level we get back is TESTER, that means the access capabilities are equivalent
  const showTesterActions = user?.user.getLevel() === UserLevel.TESTER;

  useEffect(() => {
    return () => {
      userService.cancelAllRequests();
    };
  }, [userService]);

  const handleSubmit = async (name: string, preferredName: string | null, level: UserLevel | null, roles: UserRole[] | null, password: string) => {
    if ((level === null && roles === null) || (level !== null && roles !== null)) return;

    try {
      setError(null);
      if (level !== null && roles === null) {
        const user = await userService.createUser({ name, preferredName, level, roles: null, password });
        setUser({ user, password });
      } else if (level === null && roles !== null) {
        const user = await userService.createUser({ name, preferredName, level: null, roles, password });
        setUser({ user, password });
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <Dialog
      title={user ? t('createUser.done.title', { username: user.user.getUsername() }) : t('createUser.title')}
      subtitle={user ? t('createUser.done.subtitle') : undefined}
      onDismiss={() => onClosed(user?.user.getId() ?? undefined, false)}
    >
      <DialogBody>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {user === null && (
          <CreateUserForm
            useRoles={useRoles}
            submit={{ label: t('createUser.submit'), action: handleSubmit }}
            back={{ label: t('createUser.back'), action: () => onClosed(undefined, false) }}
          />
        )}
        {user !== null && (
          <>
            <FormSummary
              user={{
                name: user.user.getUsername(),
                preferredName: user.user.getPreferredName(),
                level: useRoles ? null : user.user.getLevel(),
                roles: useRoles ? user.user.getRoles() : null,
                password: user.password,
              }}
            />
            {showTesterActions && (
              <Alert sx={{ mt: 2, alignItems: 'center' }} color="info">
                {t('createUser.done.testerTip')}
              </Alert>
            )}
          </>
        )}
      </DialogBody>
      {user !== null && (
        <DialogButtons>
          {showTesterActions && <SecondaryButton onClick={() => onClosed(user.user.getId(), true)}>{t('assignProjects')}</SecondaryButton>}
          <PrimaryButton autoFocus onClick={() => onClosed(user.user.getId(), false)}>
            {t('close')}
          </PrimaryButton>
        </DialogButtons>
      )}
    </Dialog>
  );
};
